import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Copy,
  Database,
  Eye,
  EyeOff,
  KeyRound,
  Terminal,
  Users,
} from "lucide-react";
import {
  api,
  apiBase,
  mcpBase,
  type AgentGrant,
  type APIKey,
  type Migration,
  type Project,
  type ProjectAuthPolicy,
  type UsageSummary,
} from "@/lib/api";
import { ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export function ProjectOverviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [keys, setKeys] = useState<APIKey[]>([]);
  const [migs, setMigs] = useState<Migration[]>([]);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [usersCount, setUsersCount] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [fieldCopied, setFieldCopied] = useState<string | null>(null);
  const [origins, setOrigins] = useState("");
  const [corsSaving, setCorsSaving] = useState(false);
  const [corsMessage, setCorsMessage] = useState("");
  const [authPolicy, setAuthPolicy] = useState<ProjectAuthPolicy | null>(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyMessage, setPolicyMessage] = useState("");
  const [agentGrants, setAgentGrants] = useState<AgentGrant[]>([]);
  const [agentBusy, setAgentBusy] = useState(false);
  const [mcpCopied, setMcpCopied] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, string>>({});
  const [keyBusy, setKeyBusy] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        const [p, k, m, u, users, policy, grants] = await Promise.all([
          api.getProject(id),
          api.listKeys(id),
          api.listMigrations(id),
          api.usage(id),
          api.listAppUsers(id),
          api.getProjectAuthPolicy(id),
          api.listProjectAgentGrants(id).catch(() => ({ data: [] as AgentGrant[] })),
        ]);
        setProject(p.data);
        setOrigins((p.data.allowed_origins ?? []).join("\n"));
        setKeys(k.data);
        setMigs(m.data.slice(0, 5));
        setUsage(u.data);
        setUsersCount(users.data.length);
        setAuthPolicy(policy.data);
        setAgentGrants(grants.data ?? []);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spinner label="Loading project overview…" />;
  if (!project) return error ? <ErrorBox error={error} /> : null;

  const publicKey = keys.find((k) => k.key_type === "public" && !k.revoked_at && k.revealable)
    ?? keys.find((k) => k.key_type === "public" && !k.revoked_at);
  const serviceKey = keys.find((k) => k.key_type === "service" && !k.revoked_at && k.revealable)
    ?? keys.find((k) => k.key_type === "service" && !k.revoked_at);

  const quickStartPublicKey = publicKey ? revealedKeys[publicKey.id] : undefined;
  const quickStart = `import { createClient } from "@knotree/client";

const tb = createClient({
  projectKey: "${quickStartPublicKey ?? "YOUR_PUBLIC_KEY"}",
});

const { data } = await tb.auth.signUp({
  email: "user@example.com",
  password: "password123",
});`;

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(quickStart);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  async function copyField(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setFieldCopied(label);
      window.setTimeout(() => setFieldCopied(null), 1500);
    } catch {
      /* ignore */
    }
  }

  async function saveOrigins() {
    setError(null);
    setCorsMessage("");
    setCorsSaving(true);
    try {
      const values = origins
        .split(/\r?\n/)
        .map((origin) => origin.trim())
        .filter(Boolean);
      const { data } = await api.updateProjectCORS(id, values);
      setProject(data);
      setOrigins(data.allowed_origins.join("\n"));
      setCorsMessage("Browser origins saved.");
    } catch (err) {
      setError(err);
    } finally {
      setCorsSaving(false);
    }
  }

  async function saveAuthPolicy() {
    if (!authPolicy) return;
    setError(null);
    setPolicyMessage("");
    setPolicySaving(true);
    try {
      const { data } = await api.updateProjectAuthPolicy(id, authPolicy);
      setAuthPolicy(data);
      setPolicyMessage("Hosted authentication policy saved.");
    } catch (err) {
      setError(err);
    } finally {
      setPolicySaving(false);
    }
  }

  const mcpURL = mcpBase();
  const connectAgentSnippet = JSON.stringify(
    {
      mcpServers: {
        tinybase: {
          url: mcpURL,
        },
      },
    },
    null,
    2,
  );

  async function copyMCPConfig() {
    try {
      await navigator.clipboard.writeText(connectAgentSnippet);
      setMcpCopied(true);
      window.setTimeout(() => setMcpCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  async function toggleKey(key: APIKey) {
    if (revealedKeys[key.id]) {
      setRevealedKeys((current) => {
        const next = { ...current };
        delete next[key.id];
        return next;
      });
      return;
    }
    setKeyBusy(key.id);
    setError(null);
    try {
      const response = await api.revealKey(id, key.id);
      setRevealedKeys((current) => ({ ...current, [key.id]: response.data.key }));
    } catch (err) {
      setError(err);
    } finally {
      setKeyBusy(null);
    }
  }

  async function copyKey(key: APIKey) {
    const value = revealedKeys[key.id];
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setKeyCopied(key.id);
      window.setTimeout(() => setKeyCopied(null), 1500);
    } catch {
      /* ignore */
    }
  }

  async function issueReplacement(keyType: "public" | "service") {
    setKeyBusy(`issue-${keyType}`);
    setError(null);
    try {
      const issued = await api.issueKey(id, keyType);
      const listed = await api.listKeys(id);
      setKeys(listed.data);
      setRevealedKeys((current) => ({ ...current, [issued.data.id]: issued.data.key }));
    } catch (err) {
      setError(err);
    } finally {
      setKeyBusy(null);
    }
  }

  async function revokeAgent(grantId: string) {
    setAgentBusy(true);
    setError(null);
    try {
      await api.revokeProjectAgentGrant(id, grantId);
      const { data } = await api.listProjectAgentGrants(id);
      setAgentGrants(data);
    } catch (err) {
      setError(err);
    } finally {
      setAgentBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Connect"
        title="Overview"
        description="API base, keys, and client snippets first — then traffic and project policy."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/dashboard/projects/${id}/sql`}>
                <Terminal className="h-3.5 w-3.5" />
                SQL editor
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/dashboard/projects/${id}/tables`}>
                <Database className="h-3.5 w-3.5" />
                Tables
              </Link>
            </Button>
          </div>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <section className="project-hero mb-5" aria-label="Project snapshot">
        <div className="project-hero-orb" aria-hidden />
        <div className="project-hero-icon" aria-hidden>
          <Database className="h-5 w-5" />
        </div>
        <div className="project-hero-copy">
          <div className="eyebrow">Project workspace</div>
          <h2>{project.name}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="mono-chip">schema: {project.schema_name}</span>
            <Badge variant={project.status === "active" ? "success" : "warning"} className="capitalize">
              {project.status}
            </Badge>
          </div>
        </div>
        <dl className="project-hero-meta">
          <div>
            <dt>Project ID</dt>
            <dd className="font-mono">{id.slice(0, 8)}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{new Date(project.created_at).toLocaleDateString()}</dd>
          </div>
        </dl>
      </section>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* DX band: connection + keys + snippet elevated above ops */}
        <Card className="card-connect lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-secondary" />
                Connection
              </CardTitle>
              <Badge variant="secondary">runtime</Badge>
            </div>
            <CardDescription>
              Copy API URL and key prefixes into clients. Reveal full keys below
              when you need them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <CopyField
                label="API URL"
                value={apiBase()}
                mono
                onCopy={() => void copyField("api", apiBase())}
                copied={fieldCopied === "api"}
              />
              <CopyField
                label="Public key"
                value={publicKey ? `${publicKey.key_prefix}…` : "—"}
                mono
                copper
                onCopy={
                  publicKey
                    ? () => void copyField("public-prefix", publicKey.key_prefix)
                    : undefined
                }
                copied={fieldCopied === "public-prefix"}
              />
              <CopyField
                label="Service key"
                value={
                  serviceKey
                    ? `${serviceKey.key_prefix}… (active)`
                    : "none active"
                }
                mono
                copper
                onCopy={
                  serviceKey
                    ? () =>
                        void copyField("service-prefix", serviceKey.key_prefix)
                    : undefined
                }
                copied={fieldCopied === "service-prefix"}
              />
              <CopyField
                label="Schema"
                value={project.schema_name}
                mono
                onCopy={() => void copyField("schema", project.schema_name)}
                copied={fieldCopied === "schema"}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="card-key lg:col-span-5">
          <CardHeader className="pb-3">
            <CardTitle>API keys</CardTitle>
            <CardDescription>
              Hidden by default. Service keys are privileged and must only be
              used on a server.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {[publicKey, serviceKey]
              .filter((key): key is APIKey => Boolean(key))
              .map((key) => {
                const value = revealedKeys[key.id];
                return (
                  <div
                    key={key.id}
                    className="rounded-md border border-primary-strong/20 bg-white/70 p-3"
                  >
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium capitalize">
                          {key.key_type} key
                        </div>
                        <div className="text-xs text-text-muted">
                          {key.key_type === "service"
                            ? "Server only — bypasses RLS"
                            : "Safe for browser clients — RLS applies"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {value ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => void copyKey(key)}
                          >
                            <Copy className="h-3.5 w-3.5" />
                            {keyCopied === key.id ? "Copied" : "Copy"}
                          </Button>
                        ) : null}
                        {key.revealable ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={keyBusy === key.id}
                            onClick={() => void toggleKey(key)}
                          >
                            {value ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                            {keyBusy === key.id
                              ? "Loading…"
                              : value
                                ? "Hide"
                                : "Reveal"}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={keyBusy === `issue-${key.key_type}`}
                            onClick={() =>
                              void issueReplacement(
                                key.key_type as "public" | "service",
                              )
                            }
                          >
                            Issue replacement
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="break-all rounded border border-border-standard bg-surface-container-lowest px-3 py-2 font-mono text-xs text-text-secondary">
                      {value ?? `${key.key_prefix}${"•".repeat(24)}`}
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>Quick start</CardTitle>
                <CardDescription className="mt-1">
                  Drop this into a client app. Reveal a public key above to fill
                  the snippet automatically.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">@knotree/client</Badge>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void copySnippet()}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded-lg border border-code-block-border bg-code-block-bg p-4 font-mono text-xs leading-relaxed text-code-block-text shadow-none">
              {quickStart}
            </pre>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Traffic · last 24h</CardTitle>
              <Badge variant="secondary">usage rollup</Badge>
            </div>
            <CardDescription>
              Aggregates from the platform usage pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <StatTile label="API requests" value={usage?.api_requests ?? 0} />
              <StatTile
                label="DB queries"
                value={usage?.database_queries ?? 0}
              />
              <StatTile label="Rows read" value={usage?.rows_read ?? 0} />
              <StatTile label="Rows written" value={usage?.rows_written ?? 0} />
              <StatTile label="Errors" value={usage?.error_count ?? 0} />
              <StatTile
                label="End users"
                value={usersCount}
                hint="auth accounts"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dashboard/projects/${id}/usage`}>
                  Full usage
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dashboard/projects/${id}/auth/users`}>
                  <Users className="h-3.5 w-3.5" />
                  Manage users
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dashboard/projects/${id}/observability`}>
                  Health snapshot
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-secondary" />
                Connect agent
              </CardTitle>
              <Badge variant="secondary">OAuth 2.1 + PKCE</Badge>
            </div>
            <CardDescription>
              Hosted MCP is the recommended connection for remote AI agents.
              Paste the URL-only config into your client; the first connection
              opens TinyBase sign-in and an explicit project consent screen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="space-y-3">
                <div className="dx-field-key">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-disabled">
                    MCP endpoint
                  </div>
                  <div className="mt-1 break-all font-mono text-sm text-primary-strong">
                    {mcpURL}
                  </div>
                </div>
                <ol className="space-y-2 text-sm text-text-secondary">
                  <li className="flex gap-2">
                    <span className="mono-chip shrink-0">01</span>
                    <span>Paste the config into your MCP client.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mono-chip shrink-0">02</span>
                    <span>Sign in, choose the project, and approve the requested scopes.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mono-chip shrink-0">03</span>
                    <span>Manage or revoke the connection in Agent access below.</span>
                  </li>
                </ol>
                <p className="text-xs leading-relaxed text-text-muted">
                  OAuth metadata discovery handles registration and PKCE. Never
                  paste a service key into an MCP client or browser.
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="eyebrow">Client config</div>
                    <p className="mt-1 text-xs text-text-muted">
                      Works with clients that support remote MCP over OAuth.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void copyMCPConfig()}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {mcpCopied ? "Copied" : "Copy config"}
                  </Button>
                </div>
                <pre className="overflow-x-auto rounded-lg border border-code-block-border bg-code-block-bg p-3 font-mono text-xs leading-relaxed text-code-block-text">
                  {connectAgentSnippet}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <CardTitle>Agent access</CardTitle>
            <CardDescription>
              OAuth grants for AI agents on this project. Revoking ends refresh
              tokens and denies future access immediately.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {agentGrants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connected agents yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Scopes</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentGrants.map((g) => (
                    <TableRow key={g.id}>
                      <TableCell>
                        {g.client_name}
                        {g.revoked_at ? (
                          <Badge variant="secondary" className="ml-2">
                            revoked
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {(g.scopes ?? []).join(" ")}
                      </TableCell>
                      <TableCell className="text-xs">
                        {g.last_used_at
                          ? new Date(g.last_used_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {!g.revoked_at ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={agentBusy}
                            onClick={() => void revokeAgent(g.id)}
                          >
                            Revoke
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <CardTitle>Browser origins</CardTitle>
            <CardDescription>
              One exact http(s) origin per line. An empty list denies all
              browser origins; service keys remain server-only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              className="input min-h-28 font-mono text-xs"
              value={origins}
              onChange={(event) => setOrigins(event.target.value)}
              placeholder={"http://localhost:3000\nhttps://app.example.com"}
              spellCheck={false}
            />
            <div className="flex items-center gap-3">
              <Button
                type="button"
                size="sm"
                disabled={corsSaving}
                onClick={() => void saveOrigins()}
              >
                {corsSaving ? "Saving…" : "Save origins"}
              </Button>
              {corsMessage ? (
                <span className="text-sm text-secondary">{corsMessage}</span>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {authPolicy ? (
          <Card className="lg:col-span-5">
            <CardHeader className="pb-3">
              <CardTitle>Hosted authentication policy</CardTitle>
              <CardDescription>
                Project users remain separate from Dashboard owners. Tokens are
                one-time, Project-bound, and stored only as hashes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <label className="space-y-1 text-sm font-medium">
                  Email verification
                  <select
                    className="input"
                    value={authPolicy.email_verification}
                    onChange={(event) =>
                      setAuthPolicy({
                        ...authPolicy,
                        email_verification: event.target.value as
                          | "optional"
                          | "required",
                      })
                    }
                  >
                    <option value="optional">Optional</option>
                    <option value="required">Required before sign-in</option>
                  </select>
                </label>
                <label className="space-y-1 text-sm font-medium">
                  Verification TTL (seconds)
                  <input
                    className="input"
                    type="number"
                    min={900}
                    max={86400}
                    value={authPolicy.verification_token_ttl_seconds}
                    onChange={(event) =>
                      setAuthPolicy({
                        ...authPolicy,
                        verification_token_ttl_seconds: Number(
                          event.target.value,
                        ),
                      })
                    }
                  />
                </label>
                <label className="space-y-1 text-sm font-medium">
                  Password reset TTL (seconds)
                  <input
                    className="input"
                    type="number"
                    min={300}
                    max={3600}
                    value={authPolicy.password_reset_token_ttl_seconds}
                    onChange={(event) =>
                      setAuthPolicy({
                        ...authPolicy,
                        password_reset_token_ttl_seconds: Number(
                          event.target.value,
                        ),
                      })
                    }
                  />
                </label>
              </div>
              <p className="text-xs text-text-muted">
                Password reset always revokes every active Project session; this
                safety control cannot be disabled.
              </p>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  size="sm"
                  disabled={policySaving}
                  onClick={() => void saveAuthPolicy()}
                >
                  {policySaving ? "Saving…" : "Save auth policy"}
                </Button>
                {policyMessage ? (
                  <span className="text-sm text-secondary">{policyMessage}</span>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>Recent migrations</CardTitle>
                <CardDescription className="mt-1">
                  Latest schema changes applied to this project.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dashboard/projects/${id}/sql`}>
                  Open SQL
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {migs.length === 0 ? (
              <p className="text-sm text-text-muted">
                No migrations yet. Use the SQL editor in migration mode to
                create tables.
              </p>
            ) : (
              <div className="table-wrap !shadow-none">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-surface-container-low hover:bg-surface-container-low">
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {migs.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-mono tabular-nums">
                          {m.version}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {m.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {m.source}
                        </TableCell>
                        <TableCell className="text-text-muted">
                          {new Date(m.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CopyField({
  label,
  value,
  mono,
  copper,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copper?: boolean;
  onCopy?: () => void;
  copied?: boolean;
}) {
  return (
    <div className={copper ? "dx-field-key" : "dx-field"}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-disabled">
          {label}
        </div>
        {onCopy ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-[11px] text-text-muted"
            onClick={onCopy}
            aria-label={`Copy ${label}`}
          >
            <Copy className="h-3 w-3" />
            {copied ? "Copied" : "Copy"}
          </Button>
        ) : null}
      </div>
      <div
        className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""} ${
          copper ? "text-primary-strong" : "text-text-primary"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
